/* eslint-disable no-param-reassign */
import { arrayify } from '@ethersproject/bytes'
import { parseBytes32String } from '@ethersproject/strings'
import { ChainId, Currency, currencyEquals, ETHER, Token } from '@pancakeswap/sdk'
import { createSelector } from '@reduxjs/toolkit'
import { GELATO_NATIVE } from 'config/constants'
import {
  robinhoodTestnetTokens,
  robinhoodMainnetTokens,
} from 'config/constants/tokens'
import {
  ROBINHOOD_MAINNET_CHAIN_ID,
  isRobinhoodChain,
} from 'config/constants/chains'
import { CHAIN_ID } from 'config/constants/networks'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { getRpcProvider } from 'utils/providers'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { TokenAddressMap } from 'state/types'
import {
  combinedTokenMapFromActiveUrlsSelector,
  combinedTokenMapFromOfficialsUrlsSelector,
  useUnsupportedTokenList,
} from '../state/lists/hooks'
import { NEVER_RELOAD, useSingleCallResult } from '../state/multicall/hooks'
import useUserAddedTokens, { userAddedTokenSelector } from '../state/user/hooks/useUserAddedTokens'
import { isAddress } from '../utils'
import { useBytes32TokenContract, useTokenContract } from './useContract'

const mapWithoutUrls = (tokenMap: TokenAddressMap) =>
  Object.keys(tokenMap[CHAIN_ID]).reduce<{ [address: string]: Token }>((newMap, address) => {
    newMap[address] = tokenMap[CHAIN_ID][address].token
    return newMap
  }, {})

const allTokenSelector = createSelector(
  [combinedTokenMapFromActiveUrlsSelector, userAddedTokenSelector],
  (tokenMap, userAddedTokens) => {
    return (
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (tokenMap_, token) => {
            tokenMap_[token.address] = token
            return tokenMap_
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          mapWithoutUrls(tokenMap),
        )
    )
  },
)

const allOfficialsAndUserAddedTokensSelector = createSelector(
  [combinedTokenMapFromOfficialsUrlsSelector, userAddedTokenSelector],
  (tokenMap, userAddedTokens) => {
    return (
      userAddedTokens
        // reduce into all ALL_TOKENS filtered by the current chain
        .reduce<{ [address: string]: Token }>(
          (tokenMap_, token) => {
            tokenMap_[token.address] = token
            return tokenMap_
          },
          // must make a copy because reduce modifies the map, and we do not
          // want to make a copy in every iteration
          mapWithoutUrls(tokenMap),
        )
    )
  },
)

/**
 * Returns all tokens that are from active urls and user added tokens
 * Note: GRX Chain tokens are already included via the active token list URL
 * configured in config/constants/lists.ts, so no separate fetching is needed
 */
export function useAllTokens(): { [address: string]: Token } {
  const { chainId } = useActiveWeb3React()
  const defaultTokens = useSelector(allTokenSelector)
  const userAddedTokens = useUserAddedTokens()

  return useMemo(() => {
    if (!isRobinhoodChain(chainId)) {
      return defaultTokens
    }

    const robinhoodTokens: { [address: string]: Token } = {}
    const configuredTokens =
      chainId === ROBINHOOD_MAINNET_CHAIN_ID
        ? robinhoodMainnetTokens
        : robinhoodTestnetTokens

    Object.values(configuredTokens).forEach((token) => {
      robinhoodTokens[token.address] = token
    })

    userAddedTokens
      .filter((token) => token.chainId === chainId)
      .forEach((token) => {
        robinhoodTokens[token.address] = token
      })

    return robinhoodTokens
  }, [chainId, defaultTokens, userAddedTokens])
}

/**
 * Returns all tokens that are from officials token list and user added tokens
 */
export function useOfficialsAndUserAddedTokens(): { [address: string]: Token } {
  return useSelector(allOfficialsAndUserAddedTokensSelector)
}

export function useUnsupportedTokens(): { [address: string]: Token } {
  const unsupportedTokensMap = useUnsupportedTokenList()
  return useMemo(() => mapWithoutUrls(unsupportedTokensMap), [unsupportedTokensMap])
}

export function useIsTokenActive(token: Token | undefined | null): boolean {
  const activeTokens = useAllTokens()

  if (!activeTokens || !token) {
    return false
  }

  return !!activeTokens[token.address]
}

// Check if currency is included in custom list from user storage
export function useIsUserAddedToken(currency: Currency | undefined | null): boolean {
  const userAddedTokens = useUserAddedTokens()

  if (!currency) {
    return false
  }

  return !!userAddedTokens.find((token) => currencyEquals(currency, token))
}

// parse a name or symbol from a token response
const BYTES32_REGEX = /^0x[a-fA-F0-9]{64}$/

function parseStringOrBytes32(str: string | undefined, bytes32: string | undefined, defaultValue: string): string {
  return str && str.length > 0
    ? str
    : // need to check for proper bytes string and valid terminator
    bytes32 && BYTES32_REGEX.test(bytes32) && arrayify(bytes32)[31] === 0
    ? parseBytes32String(bytes32)
    : defaultValue
}

// undefined if invalid or does not exist
// null if loading
// otherwise returns the token
export function useToken(tokenAddress?: string): Token | undefined | null {
  const { chainId } = useActiveWeb3React()
  const tokens = useAllTokens()

  const address = isAddress(tokenAddress)

  const tokenContract = useTokenContract(address || undefined, false)
  const tokenContractBytes32 = useBytes32TokenContract(address || undefined, false)
  const token: Token | undefined = address ? tokens[address] : undefined

  const useDirectRobinhoodLookup =
    Boolean(chainId && isRobinhoodChain(chainId) && address && !token)

  const tokenName = useSingleCallResult(
    token || useDirectRobinhoodLookup ? undefined : tokenContract,
    'name',
    undefined,
    NEVER_RELOAD,
  )
  const tokenNameBytes32 = useSingleCallResult(
    token || useDirectRobinhoodLookup ? undefined : tokenContractBytes32,
    'name',
    undefined,
    NEVER_RELOAD,
  )
  const symbol = useSingleCallResult(
    token || useDirectRobinhoodLookup ? undefined : tokenContract,
    'symbol',
    undefined,
    NEVER_RELOAD,
  )
  const symbolBytes32 = useSingleCallResult(
    token || useDirectRobinhoodLookup ? undefined : tokenContractBytes32,
    'symbol',
    undefined,
    NEVER_RELOAD,
  )
  const decimals = useSingleCallResult(
    token || useDirectRobinhoodLookup ? undefined : tokenContract,
    'decimals',
    undefined,
    NEVER_RELOAD,
  )

  const [directToken, setDirectToken] = useState<Token | undefined | null>(
    undefined,
  )

  useEffect(() => {
    let cancelled = false

    if (!useDirectRobinhoodLookup || !chainId || !address || !tokenContract) {
      setDirectToken(undefined)
      return () => {
        cancelled = true
      }
    }

    setDirectToken(null)

    const directContract = tokenContract.connect(
      getRpcProvider(chainId),
    )

    Promise.all([
      directContract.name(),
      directContract.symbol(),
      directContract.decimals(),
    ])
      .then(([name, tokenSymbol, tokenDecimals]) => {
        if (cancelled) return

        setDirectToken(
          new Token(
            chainId,
            address,
            Number(tokenDecimals),
            tokenSymbol || 'UNKNOWN',
            name || 'Unknown Token',
          ),
        )
      })
      .catch((error) => {
        if (cancelled) return

        console.error(
          `Failed to load Robinhood token metadata for ${address}`,
          error,
        )
        setDirectToken(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [
    address,
    chainId,
    tokenContract,
    useDirectRobinhoodLookup,
  ])

  return useMemo(() => {
    if (token) return token
    if (!chainId || !address) return undefined

    if (useDirectRobinhoodLookup) {
      return directToken
    }

    if (decimals.loading || symbol.loading || tokenName.loading) return null

    if (decimals.result) {
      return new Token(
        chainId,
        address,
        decimals.result[0],
        parseStringOrBytes32(
          symbol.result?.[0],
          symbolBytes32.result?.[0],
          'UNKNOWN',
        ),
        parseStringOrBytes32(
          tokenName.result?.[0],
          tokenNameBytes32.result?.[0],
          'Unknown Token',
        ),
      )
    }

    return undefined
  }, [
    address,
    chainId,
    decimals.loading,
    decimals.result,
    directToken,
    symbol.loading,
    symbol.result,
    symbolBytes32.result,
    token,
    tokenName.loading,
    tokenName.result,
    tokenNameBytes32.result,
    useDirectRobinhoodLookup,
  ])
}

export function useCurrency(currencyId: string | undefined): Currency | Token | null | undefined {
  const { chainId } = useActiveWeb3React()
  const normalizedCurrencyId = currencyId?.toUpperCase()

  const isNative =
    currencyId?.toLowerCase() === GELATO_NATIVE ||
    (!isRobinhoodChain(chainId) && normalizedCurrencyId === 'GRX') ||
    (isRobinhoodChain(chainId) && normalizedCurrencyId === 'ETH')

  const token = useToken(isNative ? undefined : currencyId)
  return isNative ? ETHER : token
}
